import { BoxProps } from '@mui/material';
import { ComponentType, ReactNode, useCallback } from 'react';

import { PresentationSlideProps } from '@/modules/presentation/PresentationSlide/PresentationSlide';

import { EditableNotesDisplay } from './EditableNotesDisplay';
import { EntityDiagramAndNotesSlide } from './EntityDiagramAndNotesSlide';
import { EntityDiagramAndNotesSlide_EntityFragment } from './graphql/EntityDiagramAndNotesSlide.generated';

export interface PreviewAwareEntityDiagramAndNotesSlideProps {
  entity: EntityDiagramAndNotesSlide_EntityFragment;
  slideId: string;
  deathOrderId?: string | null | undefined;
  footer?: ReactNode;
  Slide?: ComponentType<PresentationSlideProps>;
  SlideWrapper?: ComponentType<BoxProps>;
  hideNotes?: boolean;
  onUpdateNotes: (newNotes: string) => void | Promise<void>;
}

/**
 * Interactive entity diagram + notes slide for surfaces outside the presentation
 * designer — the single-entity export preview (and, via the designer's thin
 * bundle adapter, the designer itself). Turns on the diagram's edit chrome and
 * swaps in the editable-notes UI, so all the react-hook-form-aware editing code
 * lives behind this component rather than in the read-only
 * {@link EntityDiagramAndNotesSlide} the export/print path renders. Stands in for
 * the designer's `PresentationAwareEntityDiagramAndNotesSlide`.
 */
export function PreviewAwareEntityDiagramAndNotesSlide({
  entity,
  slideId,
  deathOrderId,
  footer,
  Slide,
  SlideWrapper,
  hideNotes,
  onUpdateNotes,
}: PreviewAwareEntityDiagramAndNotesSlideProps) {
  const onSubmitNotes = useCallback(
    (newNotes: string) => onUpdateNotes(newNotes),
    [onUpdateNotes]
  );

  // Stable render-prop identity so the boundary survives any future
  // memoization of EntityDiagramAndNotesSlide.
  const renderNotes = useCallback(
    (displayNotes: string) => (
      <EditableNotesDisplay
        displayNotes={displayNotes}
        onSubmit={onSubmitNotes}
        entity={entity}
        deathOrderId={deathOrderId}
      />
    ),
    [onSubmitNotes, entity, deathOrderId]
  );

  return (
    <EntityDiagramAndNotesSlide
      entity={entity}
      slideId={slideId}
      deathOrderId={deathOrderId}
      footer={footer}
      Slide={Slide}
      SlideWrapper={SlideWrapper}
      hideNotes={hideNotes}
      interactiveEditing
      renderNotes={renderNotes}
    />
  );
}
